/**
 * CashFlowChart.js — 30-day cash flow forecast SVG line chart
 *
 * Renders a day-by-day projected checking balance as an interactive
 * SVG line chart. Shows payday bumps (green triangles) and bill drops
 * (orange circles). Tap/click a day to see detail panel with events.
 *
 * Props:
 *   checkingBalance    — Current checking account balance
 *   bills              — Active bills array from Firestore
 *   paydayAnchor       — ISO date string of known payday
 *   conservativeIncome — Net income per paycheck
 */
import React, { useState, useMemo, useRef } from 'react';
import { generateForecast } from '../utils/forecast';
import { formatCurrency, formatCompact, formatDate } from '../utils/format';
import theme from '../utils/theme';

// Chart layout constants
const VIEW_W = 300;
const VIEW_H = 160;
const CHART_L = 40;   // left edge of chart area
const CHART_R = 290;   // right edge
const CHART_T = 10;    // top edge
const CHART_B = 135;   // bottom edge
const CHART_W = CHART_R - CHART_L;
const CHART_H = CHART_B - CHART_T;

export default function CashFlowChart({ checkingBalance, bills, paydayAnchor, conservativeIncome }) {
  const [activePoint, setActivePoint] = useState(null);
  const svgRef = useRef(null);

  const forecast = useMemo(() => {
    if (!paydayAnchor) return [];
    return generateForecast({
      checkingBalance: checkingBalance || 0,
      bills: bills || [],
      paydayAnchor,
      conservativeIncome: conservativeIncome || 8030,
      days: 30,
    });
  }, [checkingBalance, bills, paydayAnchor, conservativeIncome]);

  if (!forecast.length) return null;

  // Compute Y scale from balance range
  const balances = forecast.map(f => f.balance);
  const maxBal = Math.max(...balances);
  const minBal = Math.min(...balances);
  const padding = Math.max((maxBal - minBal) * 0.15, 100);
  const yMax = maxBal + padding;
  const yMin = Math.min(minBal - padding, 0); // Always show zero if close
  const yRange = yMax - yMin || 1;

  // Map data to SVG coordinates
  const toX = (i) => CHART_L + (i / 30) * CHART_W;
  const toY = (bal) => CHART_T + (1 - (bal - yMin) / yRange) * CHART_H;

  // Build polyline points
  const points = forecast.map((f, i) => `${toX(i)},${toY(f.balance)}`).join(' ');

  // Area fill polygon (line + bottom edge)
  const zeroY = toY(0);
  const areaPoints = forecast.map((f, i) => `${toX(i)},${toY(f.balance)}`).join(' ')
    + ` ${toX(forecast.length - 1)},${Math.min(zeroY, CHART_B)} ${toX(0)},${Math.min(zeroY, CHART_B)}`;

  // Has negative balances?
  const hasNegative = minBal < 0;

  // Y-axis labels (3 ticks: top, middle, bottom)
  const yTicks = [yMax, (yMax + yMin) / 2, yMin].map(v => ({
    value: v,
    y: toY(v),
    label: formatCompact(Math.round(v)),
  }));

  // X-axis labels: "Today" + every ~7 days
  const xLabels = [
    { i: 0, label: 'Today' },
    { i: 7, label: formatDate(forecast[7]?.date) },
    { i: 14, label: formatDate(forecast[14]?.date) },
    { i: 21, label: formatDate(forecast[21]?.date) },
    { i: 30, label: formatDate(forecast[30]?.date) },
  ].filter(l => l.i < forecast.length && forecast[l.i]);

  // Event markers
  const paydayMarkers = [];
  const billMarkers = [];
  forecast.forEach((f, i) => {
    f.events.forEach(ev => {
      if (ev.type === 'payday') paydayMarkers.push({ i, x: toX(i), y: toY(f.balance) });
      if (ev.type === 'bill') billMarkers.push({ i, x: toX(i), y: toY(f.balance) });
    });
  });

  // Handle click/tap → map to day index
  const handleInteraction = (e) => {
    const svg = svgRef.current;
    if (!svg) return;

    // Get click position relative to SVG
    const rect = svg.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const svgX = ((clientX - rect.left) / rect.width) * VIEW_W;

    // Map to day index
    const dayIndex = Math.round(((svgX - CHART_L) / CHART_W) * 30);
    const clamped = Math.max(0, Math.min(dayIndex, forecast.length - 1));

    setActivePoint(prev => prev === clamped ? null : clamped);
  };

  const activeData = activePoint != null ? forecast[activePoint] : null;

  return (
    <div className="card cashflow-card">
      <h3>30-Day Cash Flow</h3>

      <div className="cashflow-chart-wrap">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          onClick={handleInteraction}
          onTouchStart={handleInteraction}
        >
          {/* Danger zone: red rect below zero line */}
          {hasNegative && (
            <rect
              x={CHART_L} y={zeroY}
              width={CHART_W} height={CHART_B - zeroY}
              fill={theme.danger} opacity="0.08"
            />
          )}

          {/* Zero line */}
          {hasNegative && (
            <line
              x1={CHART_L} y1={zeroY} x2={CHART_R} y2={zeroY}
              stroke={theme.danger} strokeWidth="0.5" strokeDasharray="3,2"
            />
          )}

          {/* Area fill under balance line */}
          <polygon
            points={areaPoints}
            fill={theme.success} opacity="0.12"
          />

          {/* Balance line */}
          <polyline
            points={points}
            fill="none"
            stroke={theme.success}
            strokeWidth="1.5"
            strokeLinejoin="round"
          />

          {/* Payday markers — green upward triangles */}
          {paydayMarkers.map((m, idx) => (
            <polygon
              key={`pay-${idx}`}
              points={`${m.x},${m.y - 6} ${m.x - 3.5},${m.y} ${m.x + 3.5},${m.y}`}
              fill={theme.success}
            />
          ))}

          {/* Bill markers — orange circles */}
          {billMarkers.map((m, idx) => (
            <circle
              key={`bill-${idx}`}
              cx={m.x} cy={m.y}
              r="2.5"
              fill={theme.warning}
            />
          ))}

          {/* Active point indicator */}
          {activePoint != null && (
            <>
              <line
                x1={toX(activePoint)} y1={CHART_T}
                x2={toX(activePoint)} y2={CHART_B}
                stroke={theme.textMuted} strokeWidth="0.5" strokeDasharray="2,2"
              />
              <circle
                cx={toX(activePoint)} cy={toY(forecast[activePoint].balance)}
                r="3.5"
                fill={theme.primary} stroke="#fff" strokeWidth="1"
              />
            </>
          )}

          {/* Y-axis labels */}
          {yTicks.map((t, idx) => (
            <text
              key={idx}
              x={CHART_L - 4} y={t.y + 3}
              className="cashflow-y-label"
              textAnchor="end"
            >
              {t.label}
            </text>
          ))}

          {/* X-axis labels */}
          {xLabels.map((l, idx) => (
            <text
              key={idx}
              x={toX(l.i)}
              y={CHART_B + 12}
              className="cashflow-x-label"
              textAnchor="middle"
            >
              {l.label}
            </text>
          ))}
        </svg>
      </div>

      {/* Legend */}
      <div className="cashflow-legend">
        <span className="cashflow-legend-item">
          <span className="cashflow-legend-dot payday" /> Payday
        </span>
        <span className="cashflow-legend-item">
          <span className="cashflow-legend-dot bill" /> Bill Due
        </span>
      </div>

      {/* Detail panel — visible when a day is tapped */}
      {activeData && (
        <div className="cashflow-detail">
          <div className="cashflow-detail-header">
            <span className="cashflow-detail-date">{formatDate(activeData.date)}</span>
            <span className={`cashflow-detail-balance ${activeData.balance >= 0 ? 'positive' : 'negative'}`}>
              {formatCurrency(activeData.balance, false)}
            </span>
          </div>
          {activeData.events.length > 0 && (
            <div className="cashflow-detail-events">
              {activeData.events.map((ev, idx) => (
                <div key={idx} className={`cashflow-detail-event ${ev.type}`}>
                  <span>{ev.name}</span>
                  <span>{ev.type === 'payday' ? '+' : '-'}{formatCurrency(ev.amount, false)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
